import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import DataTable from '../../reusable/DataTable/DataTable';
import StatusChip from '../../reusable/StatusChip/StatusChip';
import Button from '../../reusable/Button/Button';
import ExportDialog from '../../reusable/ExportDialog/ExportDialog';
import { usd } from '../../utilities/format';
import { debounce } from '../../utilities/debounce';
import { saveBlob } from '../../utilities/download';
import { t } from '../../i18n/i18n';
import {
  useGetTransactionsQuery,
  useLazyExportTransactionsQuery,
} from '../../store/api/transactionsApi';
import './PaymentHistory.scss';

const SEARCH_DELAY = 300;
const PAGE_SIZE = 10;
/** Offered in the pager. */
const PAGE_SIZE_OPTIONS = [10, 20, 30, 50];

const filename = ({ start_date: from, end_date: to }) =>
  `transactions${from ? `-${from}` : ''}${to ? `-${to}` : ''}.csv`;

/** Value casing is the backend's own — the label is just it title-cased. */
const STATUS_OPTIONS = [
  { label: 'All', value: null },
  { label: 'Pending', value: 'pending' },
  { label: 'Refunded', value: 'Refunded' },
  { label: 'Succeeded', value: 'succeeded' },
  { label: 'Failed', value: 'failed' },
  { label: 'Expired', value: 'Expired' },
];

/** `amount` is in minor units — the old payment page divides by 100 to print it. */

/** `Account_balance` is the raw figure; the design prints it as "100K Challenge". */
const accountSize = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n
    ? t('{{size}}K Challenge', { size: (n / 1000).toLocaleString('en-US') })
    : '—';
};

/** "12 Mar 2026", the format the Purchase date column draws. */
const DATE_FORMAT = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

const purchaseDate = (value) => {
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? DATE_FORMAT.format(parsed) : value ?? '—';
};

/**
 * Where "Buy again" sends a row — the checkout, with the plan and size of the
 * order being repeated. Matched there by name and balance rather than by id,
 * so a row is worth repeating even after the catalogue has been re-issued;
 * see the reorder effect in TradingAssessment.
 *
 * Only the plan and the size travel. The add-ons in `meta_data` (EA, raw
 * spread, payout policy) are priced per plan and would have to be re-resolved
 * against the current catalogue to be trusted, so the checkout defaults them
 * and the trader confirms — rather than this quietly re-buying an add-on at a
 * price nobody has quoted.
 */
const buyAgainHref = (row) => {
  // `buy_again` names the catalogue's own challenge and plan; the row's own
  // `plan` ("Alpha Pro 8% Legacy") and size stay as the fallback match.
  const again = row?.buy_again;
  const query = new URLSearchParams({ plan: String(again?.plan_type ?? row?.plan ?? '').trim() });
  if (again?.challenge_id != null) query.set('challenge', String(again.challenge_id));
  const size = Number(row?.account_size);
  if (Number.isFinite(size) && size > 0) query.set('size', String(size));
  return `/trading-assessment?${query}`;
};

/**
 * A payment that went through. The filter's own value is "succeeded"; the
 * other spellings are what older rows and other gateways have sent.
 */
const SUCCEEDED = new Set(['succeeded', 'success', 'successful', 'paid', 'completed']);

/**
 * A payment that was attempted and did not go through — the gateway's own
 * spellings, as above.
 */
const FAILED = new Set(['failed', 'failure', 'declined', 'rejected']);

const statusOf = (row) => String(row?.payment_status ?? '').trim().toLowerCase();
const isSucceeded = (row) => SUCCEEDED.has(statusOf(row));
const isFailed = (row) => FAILED.has(statusOf(row));

/**
 * Which lines offer "Buy again".
 *
 * Succeeded, because repeating a good order is the common case — and failed,
 * because a payment that fell over is the one a trader most wants another go
 * at, and it is what the other desks offer. Both need a plan name: with none
 * there is nothing to repeat.
 *
 * Still left out: pending (the order is alive, and a second one would be a
 * duplicate rather than a repeat), expired, and refunded.
 */
/** `buy_again.reason` → the words on its tag. Unknown codes are spelled out. */
const BUY_AGAIN_REASONS = {
  plan_retired: 'Plan retired',
  size_retired: 'Size retired',
};
const buyAgainReason = (code) =>
  BUY_AGAIN_REASONS[code] ??
  String(code).replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

const canBuyAgain = (row) =>
  // The backend says whether the order can still be bought (`buy_again`, e.g.
  // false for a retired plan); rows from before it sent that keep the old rule.
  row?.buy_again
    ? row.buy_again.available === true
    : (isSucceeded(row) || isFailed(row)) && Boolean(String(row?.plan ?? '').trim());

/** The backend sends "N/A" or null rather than omitting an unissued invoice. */
const invoiceUrl = (row) => {
  const url = row?.invoice_url || row?.invoice;
  return url && url !== 'N/A' ? url : null;
};

const formatPaymentStatus = (status) => {
  if (!status) return '—';
  const str = String(status).trim();
  return str
    .replace(/_/g, ' ')
    .replace(/\b[a-z]/g, (char) => char.toUpperCase());
};

const icon = (name) => ({ '--icon': `url(/icons/${name}.svg)` });

const KNOWN_LABELS = {
  ea: 'EA',
  raw_spread: 'Raw Spread',
  platform: 'Platform',
  alpha_pro_variant: 'Alpha Pro Variant',
  payout_policy: 'Payout Policy',
  swap_free: 'Swap Free',
};

const formatMetaLabel = (key) => {
  const lower = key.toLowerCase();
  if (KNOWN_LABELS[lower]) return KNOWN_LABELS[lower];
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatMetaValue = (value) => {
  if (value === true || value === 'true') return 'Yes';
  if (value === false || value === 'false') return 'No';
  if (value === null || value === undefined || value === '') return 'N/A';
  return String(value);
};

const getTransactionRowId = (row, i) =>
  row?.payment_id || row?.transaction_id || row?.id || (i != null ? `tx-${i}` : undefined);

const parseMetaData = (row) => {
  const raw = row?.meta_data ?? row?.metadata ?? row?.metaData ?? row?.meta;
  if (raw) {
    if (typeof raw === 'object') return raw;
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') return parsed;
    } catch {}
  }

  // Check if properties exist directly on row
  const direct = {};
  if (row?.ea !== undefined || row?.EAs !== undefined || row?.EA !== undefined) {
    direct.ea = row.ea ?? row.EAs ?? row.EA;
  }
  if (row?.raw_spread !== undefined) direct.raw_spread = row.raw_spread;
  if (row?.platform !== undefined) direct.platform = row.platform;
  if (row?.alpha_pro_variant !== undefined) direct.alpha_pro_variant = row.alpha_pro_variant;
  if (row?.payout_policy !== undefined || row?.payout_type !== undefined) {
    direct.payout_policy = row.payout_policy ?? row.payout_type;
  }
  if (row?.swap_free !== undefined) direct.swap_free = row.swap_free;

  if (Object.keys(direct).length > 0) return direct;
  return null;
};

export default function PaymentHistory() {
  const [params, setParams] = useState({
    page: 1,
    page_size: PAGE_SIZE,
    search: '',
    status: null,
  });
  const [exporting, setExporting] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const toggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const navigate = useNavigate();

  const columns = useMemo(
    () => [
      {
        key: 'plan',
        header: 'Plan',
        width: 14,
        sortable: true,
        render: (r) => {
          const rowId = getTransactionRowId(r);
          const isOpen = expandedId === rowId;
          return (
            <div className="ph-plan-cell">
              <button
                type="button"
                className={`ph-expand-toggle ${isOpen ? 'is-open' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpand(rowId);
                }}
                aria-expanded={isOpen}
                aria-label={isOpen ? t('Collapse details') : t('Expand details')}
                title={isOpen ? t('Collapse details') : t('Expand details')}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="ph-expand-icon"
                >
                  <path
                    d="M3 4.5L6 7.5L9 4.5"
                    stroke="currentColor"
                    strokeWidth="1.3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <span className="ph-plan-name">{r.plan}</span>
            </div>
          );
        },
      },
      {
        key: 'account_login_id',
        header: 'Account / Login',
        width: 12,
        value: (r) => (r.account_login_id ? `#${r.account_login_id}` : '—'),
        sortable: true,
      },
      {
        key: 'account_size',
        header: 'Account size',
        width: 12,
        value: (r) => accountSize(r.account_size),
        sortValue: (r) => Number(r.account_size),
        sortable: true,
      },
      { key: 'payment_id', header: 'Transaction ID', width: 16 },
      {
        key: 'purchase_date',
        header: 'Purchase date',
        width: 13,
        value: (r) => purchaseDate(r.purchase_date),
        sortValue: (r) => Date.parse(r.purchase_date),
        sortable: true,
      },
      {
        key: 'amount_value',
        header: 'Amount',
        width: 11,
        value: (r) => usd(Number(r.amount_value), { decimals: 2 }),
        sortValue: (r) => Number(r.amount_value),
        tone: () => 'primary',
        sortable: true,
      },
      {
        key: 'payment_status',
        header: 'Status',
        width: 11,
        value: (r) => formatPaymentStatus(r.payment_status),
        render: (r) => <StatusChip>{formatPaymentStatus(r.payment_status)}</StatusChip>,
        sortable: true,
      },
      {
        key: 'invoice_url',
        header: 'Invoice',
        width: 11,
        searchable: false,
        render: (r) =>
          invoiceUrl(r) ? (
            <Button
              variant="pill"
              paddingInline={16}
              href={invoiceUrl(r)}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              icon={<span className="icon" style={icon('export')} />}
              iconPlacement="end"
            >
              {t('Invoice')}
            </Button>
          ) : (
            <span className="ph-invoice__not-issued">{t('Not issued')}</span>
          ),
      },
      {
        key: 'buy_again',
        header: 'Reorder',
        width: 12,
        searchable: false,
        // Succeeded and failed orders — see `canBuyAgain`.
        render: (r) =>
          canBuyAgain(r) ? (
            <Button
              variant="toolbar"
              paddingInline={16}
              className="ph-buy-again"
              onClick={(e) => {
                e.stopPropagation();
                navigate(buyAgainHref(r));
              }}
              icon={<span className="icon" style={icon('nav-setup')} />}
              iconPlacement="end"
            >
              {t('Buy again')}
            </Button>
          ) : r.buy_again?.available === false && r.buy_again.reason ? (
            // Why it can't be bought again (e.g. a retired plan): the button's
            // own shape and width, in the warning colour, and not clickable.
            <span className="ph-buy-again ph-buy-again--off" role="note">
              {t(buyAgainReason(r.buy_again.reason))}
            </span>
          ) : (
            <span className="ph-invoice__not-issued">—</span>
          ),
      },
    ],
    [expandedId, navigate]
  );

  const renderExpanded = (row) => {
    const meta = parseMetaData(row);
    const entries = meta
      ? Object.entries(meta).filter(([key]) => Boolean(KNOWN_LABELS[key.toLowerCase()]))
      : [];

    if (!entries.length) {
      return (
        <div className="ph-expanded-panel">
          <span className="ph-expanded-empty">{t('No additional details available')}</span>
        </div>
      );
    }

    return (
      <div className="ph-expanded-panel">
        {entries.map(([key, value]) => (
          <div key={key} className="ph-expanded-item">
            <span className="ph-expanded-label">{t(formatMetaLabel(key))}:</span>
            <span className="ph-expanded-value">{t(formatMetaValue(value))}</span>
          </div>
        ))}
      </div>
    );
  };

  const { data, isFetching, isError } = useGetTransactionsQuery(params);
  const [runExport, { isFetching: isExporting }] = useLazyExportTransactionsQuery();

  const exportTransactions = async (range) => {
    try {
      saveBlob(await runExport(range).unwrap(), filename(range));
      setExporting(false);
    } catch {
      message.error(t('Could not export your transactions'));
    }
  };

  /** Anything but the page number is a new result set, so it lands back on page 1. */
  const setParam = (key, value) => setParams((prev) => ({ ...prev, page: 1, [key]: value }));

  /** Every keystroke is a request, so the box settles before the query refires. */
  const onSearch = useMemo(
    () =>
      debounce(
        (value) => setParams((prev) => ({ ...prev, page: 1, search: value.trim() })),
        SEARCH_DELAY
      ),
    []
  );

  const rows = data?.results ?? [];

  return (
    <div className="payment-history">
      <DataTable
        title="Transactions"
        icon="table-payments"
        columns={columns}
        rows={rows}
        getRowId={getTransactionRowId}
        expandedId={expandedId}
        renderExpanded={renderExpanded}
        onRowClick={(row) => {
          toggleExpand(getTransactionRowId(row));
        }}
        page={params.page}
        onPageChange={(page, pageSize) =>
          setParams((prev) => ({
            ...prev,
            page,
            page_size: pageSize ?? prev.page_size ?? PAGE_SIZE,
          }))
        }
        total={data?.count ?? 0}
        pageSize={params.page_size ?? data?.page_size ?? PAGE_SIZE}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        searchPlaceholder="Search payments"
        onSearch={onSearch}
        filter={{
          options: STATUS_OPTIONS,
          value: params.status,
          onChange: (value) => setParam('status', value),
        }}
        onExport={() => setExporting(true)}
        loading={isFetching}
        emptyMessage={
          isError ? 'We could not load your transactions' : 'No transactions match this filter'
        }
      />

      <ExportDialog
        open={exporting}
        title={t('Export transactions')}
        statusOptions={STATUS_OPTIONS.map((o) => ({ ...o, label: t(o.label) }))}
        busy={isExporting}
        onExport={exportTransactions}
        onClose={() => setExporting(false)}
      />
    </div>
  );
}
